import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FaLinkedin, FaGithub } from 'react-icons/fa';
import Paul from '../assets/images/paul.jpeg';
import Harrison from '../assets/images/harrison.png';
import Hosuke from '../assets/images/hosuke.png';
import Bill from '../assets/images/bill.jpeg';
import Daniel from '../assets/images/daniel.jpeg';
import Junyi from '../assets/images/junyi.jpeg';

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const hexagonRotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const AvatarImage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 3;
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.1);
  }
`;

// Define gradient colors for different roles
const roleGradients = {
  founder: "linear-gradient(135deg, #FFD700, #FFA500)", // Gold gradient
  advisor: "linear-gradient(135deg, #6366f1, #8b5cf6)", // Purple gradient (original)
  senior: "linear-gradient(135deg, #10B981, #059669)", // Green gradient
  junior: "linear-gradient(135deg, #3B82F6, #1D4ED8)"  // Blue gradient
};

const Avatar = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 1rem;
  position: relative;
  animation: ${float} 6s ease-in-out infinite;

  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    background: ${props => roleGradients[props.role] || roleGradients.advisor};
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    animation: ${hexagonRotate} 8s linear infinite;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #0f172a;
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    z-index: 2;
  }

  @media (min-width: 768px) {
    width: 120px;
    height: 120px;
    margin: 0 auto 1.5rem;
  }
`;

const MemberCard = styled(motion.div)`
  width: 100%;
  max-width: 320px;
  height: 500px; 
  padding: 1.5rem;
  margin: 0;
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 16px;
  text-align: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  &:hover {
    transform: translateY(-10px);
    border-color: #6366f1;
    box-shadow: 0 10px 30px -10px rgba(99, 102, 241, 0.3);

    ${Avatar}::before {
      animation: ${hexagonRotate} 4s linear infinite;
    }
  }

  @media (min-width: 768px) {
    width: calc(50% - 2rem);
    padding: 2rem;
    height: 520px; 
  }

  @media (min-width: 1024px) {
    width: 280px;
    height: 520px; 
  }
`;

const ContentWrapper = styled.div`
  gap: 1rem;
  
  @media (min-width: 768px) {
    gap: 1.5rem;
  }
`;

const Name = styled.h3`
  font-size: 1.1rem;
  margin: 0.5rem 0;
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Role = styled.h4`
  font-size: 0.9rem;
  margin: 0.5rem 0;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    margin: 0.5rem 0 1rem;
  }
`;

const Bio = styled.p`
  font-size: 0.85rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 6;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0.5rem 0;
  height: 150px;
  
  @media (min-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.6;
    -webkit-line-clamp: 5;
    height: 160px;
  }
`;

const SocialLinks = styled.div`
  gap: 0.75rem;
  margin-top: 1rem;
  
  a {
    font-size: 1.1rem;
    
    @media (min-width: 768px) {
      font-size: 1.25rem;
      margin-top: 1.5rem;
    }
  }
`;

const Container = styled.section`
  padding: 4rem 1rem;
  min-height: 100vh;
  overflow-x: hidden;
  
  @media (min-width: 768px) {
    padding: 6rem 2rem;
  }
`;

const TeamWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const Title = styled.h2`
  text-align: center;
  font-size: 2rem;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 4rem;
  }
`;

const TeamLevel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  margin-bottom: 3rem;
  position: relative;
  
  @media (min-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 2rem;
    margin-bottom: 4rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -1.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 1.5rem;
    background: linear-gradient(to bottom, #6366f1, transparent);
    
    @media (min-width: 768px) {
      bottom: -2rem;
      height: 2rem;
    }
  }
`;

const TeamChart = () => {
  const teamMembers = {
  };
};

export default TeamChart; 